import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../components/FormField.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type {
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  PlatformProviderModelListQuery,
  ThinkingLevel,
  ToastMessage,
  VelugaModelCatalogItem,
} from '../../types.js';
import { PROVIDERS } from './base-urls.js';
import {
  manualNativeWebSearchEnabled,
  manualRoutingSafety,
  otherProviderDefault,
  supportsManualNativeWebSearch,
} from './manual-provider-policy-form.js';
import {
  manualThinkingLevelOptions,
  manualThinkingLevelSelection,
  manualThinkingLevelsPayload,
  manualThinkingLevelsSupported,
} from './manual-thinking-levels-form.js';

const emptyCapabilities = {
  reasoning: false,
  vision: false,
  documentInput: false,
  toolUse: true,
  streaming: true,
  structuredOutput: true,
  webSearchNative: false,
};

const emptyRoutingSafety: VelugaModelCatalogItem['routing']['safety'] = {
  allowAdaptiveForRisk: [],
  disallowForDataClassifications: [],
  requireStrongForFinalAnswer: false,
  requireStrongForCompliance: false,
};

interface EditorState {
  provider: PlatformProviderKeyProvider;
  modelKey: string;
  displayName: string;
  description: string;
  upstreamModelId: string;
  executionMode: 'chat-completions' | 'responses';
  capabilities: typeof emptyCapabilities;
  thinkingLevels?: ThinkingLevel[];
  contextWindow: string;
  maxOutputTokens: string;
  sourceUrl: string;
  verifiedAt: string;
  defaultForProvider: boolean;
  routingSafetyEnabled: boolean;
  routingSafety: VelugaModelCatalogItem['routing']['safety'];
}
function blankEditor(): EditorState {
  return {
    provider: 'openai',
    modelKey: '',
    displayName: '',
    description: '',
    upstreamModelId: '',
    executionMode: 'chat-completions',
    capabilities: { ...emptyCapabilities },
    contextWindow: '128000',
    maxOutputTokens: '8192',
    sourceUrl: '',
    verifiedAt: new Date().toISOString().slice(0, 10),
    defaultForProvider: false,
    routingSafetyEnabled: false,
    routingSafety: { ...emptyRoutingSafety },
  };
}

function editorFor(model: PlatformProviderModelDefinition): EditorState {
  return {
    provider: model.provider,
    modelKey: model.modelKey,
    displayName: model.displayName,
    description: model.description ?? '',
    upstreamModelId: model.upstreamModelId,
    executionMode: model.executionMode,
    capabilities: {
      reasoning: model.capabilities.reasoning,
      vision: model.capabilities.vision,
      documentInput: model.capabilities.documentInput ?? false,
      toolUse: model.capabilities.toolUse,
      streaming: model.capabilities.streaming,
      structuredOutput: model.capabilities.structuredOutput,
      webSearchNative: manualNativeWebSearchEnabled(model.capabilities),
    },
    ...(model.thinkingLevels ? { thinkingLevels: model.thinkingLevels } : {}),
    contextWindow: String(model.limits.contextWindow),
    maxOutputTokens: String(model.limits.maxOutputTokens),
    sourceUrl: model.pricingEvidence.sourceUrl,
    verifiedAt: model.pricingEvidence.verifiedAt.slice(0, 10),
    defaultForProvider: model.defaultForProvider ?? false,
    routingSafetyEnabled: model.routingSafety !== undefined,
    routingSafety: model.routingSafety
      ? {
          ...model.routingSafety,
          allowAdaptiveForRisk: [...model.routingSafety.allowAdaptiveForRisk],
          disallowForDataClassifications: [...model.routingSafety.disallowForDataClassifications],
        }
      : { ...emptyRoutingSafety },
  };
}

function modelIdentity(model: Pick<PlatformProviderModelDefinition, 'provider' | 'modelKey'>) {
  return `${model.provider}:${model.modelKey}`;
}

const COMPACT_WORKBENCH_WIDTH = 760;

export function ManualProviderModelsPanel({
  http,
  models,
  setModels,
  selected,
  setSelected,
  busy,
  setBusy,
  onToast,
  onOpenDeployment,
  onChanged,
}: {
  http: AdminHttpClient;
  models: PlatformProviderModelDefinition[];
  setModels: (models: PlatformProviderModelDefinition[]) => void;
  selected?: PlatformProviderModelDefinition;
  setSelected: (model?: PlatformProviderModelDefinition) => void;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  onOpenDeployment: (model: PlatformProviderModelDefinition) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const translateError = useCallback(
    (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {})),
    [t]
  );
  const [editor, setEditor] = useState<EditorState>(blankEditor);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState<'' | PlatformProviderKeyProvider>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'all' | 'draft' | 'active' | 'disabled'>('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [compactView, setCompactView] = useState<'list' | 'detail'>('list');
  const workbenchRef = useRef<HTMLDivElement>(null);
  const listPaneRef = useRef<HTMLDivElement>(null);
  const detailPaneRef = useRef<HTMLFormElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const newButtonRef = useRef<HTMLButtonElement>(null);
  const firstFilterRef = useRef<HTMLSelectElement>(null);
  const modelButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const lastSelectedIdentityRef = useRef<string>();
  const immutable = selected?.status === 'active';
  const webSearchSupported = supportsManualNativeWebSearch(editor.provider);
  const conflictingDefault = otherProviderDefault(models, editor.provider, selected?.modelKey);
  const thinkingLevelOptions = useMemo(
    () => manualThinkingLevelOptions(editor.provider, editor.capabilities.reasoning, editor.upstreamModelId),
    [editor.capabilities.reasoning, editor.provider, editor.upstreamModelId]
  );
  const thinkingLevelSelection = manualThinkingLevelSelection(
    thinkingLevelOptions,
    editor.thinkingLevels
  );

  const reload = useCallback(
    async (identity?: Pick<PlatformProviderModelDefinition, 'provider' | 'modelKey'>) => {
      setLoading(true);
      try {
        const query: PlatformProviderModelListQuery = {
          limit: 20,
          offset,
          ...(providerFilter ? { provider: providerFilter } : {}),
          // Default view hides retired models; "all" shows them again.
          ...(statusFilter === ''
            ? { excludeDisabled: true }
            : statusFilter === 'all'
              ? {}
              : { status: statusFilter }),
        };
        const response = await http.listPlatformProviderModels(query);
        setModels(response.items);
        setTotal(response.total);
        if (identity) {
          const next = response.items.find(
            (item) => item.provider === identity.provider && item.modelKey === identity.modelKey
          );
          setSelected(next);
          if (next) setEditor(editorFor(next));
        }
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, translateError) });
      } finally {
        setLoading(false);
      }
    },
    [http, offset, onToast, providerFilter, setModels, setSelected, statusFilter, translateError]
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const activeDeployments = useMemo(
    () => selected?.deployments?.filter((deployment) => deployment.status === 'active') ?? [],
    [selected]
  );

  function resetListForQueryChange() {
    if (listPaneRef.current) listPaneRef.current.scrollTop = 0;
    setCompactView('list');
  }

  function selectModel(model?: PlatformProviderModelDefinition) {
    lastSelectedIdentityRef.current = model ? modelIdentity(model) : undefined;
    setSelected(model);
    setEditor(model ? editorFor(model) : blankEditor());
    if (detailPaneRef.current) detailPaneRef.current.scrollTop = 0;
    setCompactView('detail');
    const workbenchWidth = workbenchRef.current?.clientWidth ?? Number.POSITIVE_INFINITY;
    if (workbenchWidth <= COMPACT_WORKBENCH_WIDTH) {
      requestAnimationFrame(() => backButtonRef.current?.focus({ preventScroll: true }));
    }
  }

  function returnToList() {
    setCompactView('list');
    requestAnimationFrame(() => {
      const selectedButton = lastSelectedIdentityRef.current
        ? modelButtonRefs.current.get(lastSelectedIdentityRef.current)
        : undefined;
      (selectedButton ?? newButtonRef.current ?? firstFilterRef.current)?.focus({
        preventScroll: true,
      });
    });
  }

  function updateCapability(name: keyof typeof emptyCapabilities, value: boolean) {
    setEditor((current) => ({
      ...current,
      capabilities: { ...current.capabilities, [name]: value },
    }));
  }

  function definitionInput() {
    const routingSafety = manualRoutingSafety(editor.routingSafetyEnabled, editor.routingSafety);
    const thinkingLevels = manualThinkingLevelsPayload(
      thinkingLevelOptions,
      thinkingLevelSelection,
      selected?.thinkingLevels
    );
    return {
      provider: editor.provider,
      modelKey: editor.modelKey.trim(),
      displayName: editor.displayName.trim(),
      ...(editor.description.trim() ? { description: editor.description.trim() } : {}),
      upstreamModelId: editor.upstreamModelId.trim(),
      executionMode: editor.executionMode,
      capabilities: editor.capabilities,
      ...(thinkingLevels ? { thinkingLevels } : {}),
      limits: {
        contextWindow: Number(editor.contextWindow),
        maxOutputTokens: Number(editor.maxOutputTokens),
      },
      pricingEvidence: {
        sourceUrl: editor.sourceUrl.trim(),
        verifiedAt: new Date(`${editor.verifiedAt}T00:00:00.000Z`).toISOString(),
      },
      ...(routingSafety ? { routingSafety } : {}),
      defaultForProvider: editor.defaultForProvider,
    };
  }

  async function saveDefinition(event: FormEvent) {
    event.preventDefault();
    if (
      manualThinkingLevelsSupported(thinkingLevelOptions) &&
      editor.thinkingLevels !== undefined &&
      thinkingLevelSelection.length === 0
    ) {
      onToast({ tone: 'error', message: t('manualProviderModels.thinkingLevelsRequired') });
      return;
    }
    setBusy(true);
    try {
      const input = definitionInput();
      const update = {
        displayName: input.displayName,
        ...(input.description ? { description: input.description } : {}),
        upstreamModelId: input.upstreamModelId,
        executionMode: input.executionMode,
        capabilities: input.capabilities,
        ...(input.thinkingLevels ? { thinkingLevels: input.thinkingLevels } : {}),
        limits: input.limits,
        pricingEvidence: input.pricingEvidence,
        ...(input.routingSafety ? { routingSafety: input.routingSafety } : {}),
        defaultForProvider: input.defaultForProvider,
      };
      const saved = selected
        ? await http.updatePlatformProviderModel(selected.provider, selected.modelKey, {
            ...update,
            expectedRevision: selected.revision,
          })
        : await http.createPlatformProviderModel(input);
      setSelected(saved);
      setEditor(editorFor(saved));
      await reload(saved);
      onToast({ tone: 'success', message: t('manualProviderModels.saved') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (
      !selected ||
      !window.confirm(t('manualProviderModels.disableConfirm', { count: activeDeployments.length }))
    )
      return;
    setBusy(true);
    try {
      await http.disablePlatformProviderModel(selected.provider, selected.modelKey);
      await Promise.all([reload(selected), onChanged()]);
      onToast({ tone: 'success', message: t('manualProviderModels.disabled') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="panel manual-provider-models"
      aria-labelledby="manual-provider-models-title"
    >
      <div className="section-heading">
        <div>
          <h2 id="manual-provider-models-title">{t('manualProviderModels.title')}</h2>
          <p className="screen-help">{t('manualProviderModels.help')}</p>
        </div>
        <button
          ref={newButtonRef}
          type="button"
          className="secondary"
          onClick={() => selectModel()}
        >
          {t('manualProviderModels.new')}
        </button>
      </div>
      <p className="sr-status" aria-live="polite">
        {loading
          ? t('manualProviderModels.loading')
          : t('manualProviderModels.loaded', { count: models.length })}
      </p>
      <div className="manual-model-filters">
        <label>
          {t('manualProviderModels.filterProvider')}
          <select
            ref={firstFilterRef}
            value={providerFilter}
            onChange={(event) => {
              setProviderFilter(event.target.value as typeof providerFilter);
              setOffset(0);
              resetListForQueryChange();
            }}
          >
            <option value="">{t('manualProviderModels.statusCurrent')}</option>
            <option value="all">{t('common.all')}</option>
            {PROVIDERS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('manualProviderModels.filterStatus')}
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as typeof statusFilter);
              setOffset(0);
              resetListForQueryChange();
            }}
          >
            <option value="">{t('common.all')}</option>
            {(['draft', 'active', 'disabled'] as const).map((item) => (
              <option key={item} value={item}>
                {t(`manualProviderModels.status.${item}`)}
              </option>
            ))}
          </select>
        </label>
        <div className="form-actions">
          <button
            type="button"
            className="secondary"
            disabled={offset === 0 || loading}
            onClick={() => {
              setOffset(Math.max(0, offset - 20));
              resetListForQueryChange();
            }}
          >
            {t('common.previous')}
          </button>
          <span>
            {t('manualProviderModels.pageSummary', {
              from: total === 0 ? 0 : offset + 1,
              to: Math.min(offset + models.length, total),
              total,
            })}
          </span>
          <button
            type="button"
            className="secondary"
            disabled={offset + models.length >= total || loading}
            onClick={() => {
              setOffset(offset + 20);
              resetListForQueryChange();
            }}
          >
            {t('common.next')}
          </button>
        </div>
      </div>
      <div
        ref={workbenchRef}
        className="manual-model-workbench"
        data-compact-view={compactView}
        aria-label={t('manualProviderModels.workbenchLabel')}
      >
        <div
          ref={listPaneRef}
          className="credential-list manual-model-list"
          role="region"
          aria-label={t('manualProviderModels.listLabel')}
        >
          {models.length === 0 && !loading ? <p>{t('manualProviderModels.empty')}</p> : null}
          {models.map((model) => {
            const deployments = model.deployments?.filter((item) => item.status === 'active') ?? [];
            const isSelected =
              selected?.provider === model.provider && selected.modelKey === model.modelKey;
            return (
              <button
                ref={(node) => {
                  const identity = modelIdentity(model);
                  if (node) modelButtonRefs.current.set(identity, node);
                  else modelButtonRefs.current.delete(identity);
                }}
                type="button"
                className={`credential-row manual-model-row${isSelected ? ' selected' : ''}`}
                key={`${model.provider}:${model.modelKey}`}
                aria-current={isSelected ? 'true' : undefined}
                aria-label={t('manualProviderModels.modelRowLabel', {
                  name: model.displayName,
                  provider: model.provider,
                  modelKey: model.modelKey,
                })}
                onClick={() => selectModel(model)}
              >
                <span className="credential-info">
                  <span className="credential-head">
                    <strong className="manual-model-name" title={model.displayName}>
                      {model.displayName}
                    </strong>
                    <span className="badge">{t('platform.definitionKindProvider')}</span>
                    <span className={`badge ${model.status}`}>
                      {t(`manualProviderModels.status.${model.status}`)}
                    </span>
                  </span>
                  <small>
                    {model.provider} · {model.modelKey} ·{' '}
                    {t(`manualProviderModels.status.${model.status}`)}
                  </small>
                  <small>
                    {t('manualProviderModels.deploymentCount', { count: deployments.length })}
                    {deployments.length > 0
                      ? ` · ${[
                          ...new Set(
                            deployments.map((deployment) =>
                              deployment.rateVersion
                                ? deployment.rateVersion
                                : t('manualProviderModels.freeBadge')
                            )
                          ),
                        ].join(', ')}`
                      : ''}
                  </small>
                </span>
              </button>
            );
          })}
        </div>

        <form
          ref={detailPaneRef}
          className="form-grid manual-model-editor manual-model-detail"
          role="region"
          aria-label={t('manualProviderModels.detailLabel')}
          onSubmit={saveDefinition}
        >
          <button
            ref={backButtonRef}
            type="button"
            className="secondary manual-model-back"
            onClick={returnToList}
          >
            {t('manualProviderModels.backToList')}
          </button>
          <header className="manual-model-editor-header">
            <h3>
              {selected ? selected.displayName : t('manualProviderModels.createTitle')}
            </h3>
            {selected ? (
              <>
                <p>
                  {selected.provider} · {selected.modelKey} · {selected.executionMode}
                </p>
                <p>{selected.upstreamModelId}</p>
                <p>
                  {t('manualProviderModels.updatedBy', {
                    user: selected.updatedBy,
                    at: new Date(selected.updatedAt).toLocaleString(),
                  })}
                </p>
                <span className={`badge ${selected.status}`}>
                  {t(`manualProviderModels.status.${selected.status}`)}
                </span>
              </>
            ) : null}
          </header>
          {immutable ? (
            <p className="warning">{t('manualProviderModels.activeImmutableHelp')}</p>
          ) : null}
          <label>
            {t('manualProviderModels.provider')}
            <select
              value={editor.provider}
              disabled={Boolean(selected)}
              onChange={(event) =>
                setEditor((current) => ({
                  ...current,
                  provider: event.target.value as PlatformProviderKeyProvider,
                  executionMode:
                    event.target.value === 'openai' ? current.executionMode : 'chat-completions',
                  capabilities: {
                    ...current.capabilities,
                    webSearchNative: supportsManualNativeWebSearch(
                      event.target.value as PlatformProviderKeyProvider
                    )
                      ? current.capabilities.webSearchNative
                      : false,
                  },
                }))
              }
            >
              {PROVIDERS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <FormField
            label={t('manualProviderModels.modelKey')}
            value={editor.modelKey}
            disabled={Boolean(selected)}
            required
            pattern="[a-z0-9][a-z0-9-]{0,63}"
            onChange={(event) =>
              setEditor((current) => ({ ...current, modelKey: event.target.value }))
            }
          />
          <FormField
            label={t('manualProviderModels.displayName')}
            value={editor.displayName}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, displayName: event.target.value }))
            }
          />
          <FormField
            label={t('manualProviderModels.description')}
            value={editor.description}
            onChange={(event) =>
              setEditor((current) => ({ ...current, description: event.target.value }))
            }
          />
          <FormField
            label={t('manualProviderModels.upstreamModelId')}
            value={editor.upstreamModelId}
            disabled={immutable}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, upstreamModelId: event.target.value }))
            }
          />
          <label>
            {t('manualProviderModels.executionMode')}
            <select
              value={editor.executionMode}
              disabled={immutable}
              onChange={(event) =>
                setEditor((current) => ({
                  ...current,
                  executionMode: event.target.value as EditorState['executionMode'],
                }))
              }
            >
              <option value="chat-completions">chat-completions</option>
              {editor.provider === 'openai' ? <option value="responses">responses</option> : null}
            </select>
          </label>
          {selected ? (
            <>
              <p>
                <strong>{t('manualProviderModels.backendModelName')}:</strong>{' '}
                {selected.backendModelName}
              </p>
              <p>
                <strong>{t('manualProviderModels.thinkingLevels')}:</strong>{' '}
                {selected.thinkingLevels?.join(', ') ||
                  t('manualProviderModels.thinkingLevelsUnset')}
              </p>
            </>
          ) : (
            <p className="screen-help">{t('manualProviderModels.backendDerived')}</p>
          )}
          <div
            className="manual-model-capabilities"
            role="group"
            aria-label={t('manualProviderModels.capabilities')}
          >
            {(Object.keys(emptyCapabilities) as Array<keyof typeof emptyCapabilities>)
              .filter((capability) => capability !== 'webSearchNative')
              .map((capability) => (
                <label className="check-row" key={capability}>
                  <input
                    type="checkbox"
                    disabled={immutable}
                    checked={editor.capabilities[capability]}
                    onChange={(event) => updateCapability(capability, event.target.checked)}
                  />
                  <span>{t(`manualProviderModels.capability.${capability}`)}</span>
                </label>
              ))}
            <label
              className="check-row"
              title={
                webSearchSupported ? undefined : t('manualProviderModels.webSearchUnsupported')
              }
            >
              <input
                type="checkbox"
                disabled={immutable || !webSearchSupported}
                checked={editor.capabilities.webSearchNative}
                onChange={(event) => updateCapability('webSearchNative', event.target.checked)}
              />
              <span>{t('manualProviderModels.capability.webSearchNative')}</span>
            </label>
          </div>
          {manualThinkingLevelsSupported(thinkingLevelOptions) ? (
            <div
              className="manual-model-capabilities"
              role="group"
              aria-label={t('manualProviderModels.thinkingLevels')}
            >
              {thinkingLevelOptions.map((level) => (
                <label className="check-row" key={level}>
                  <input
                    type="checkbox"
                    disabled={immutable && (!selected?.thinkingLevels || selected.thinkingLevels.includes(level))}
                    checked={thinkingLevelSelection.includes(level)}
                    onChange={(event) =>
                      setEditor((current) => ({
                        ...current,
                        thinkingLevels: thinkingLevelOptions.filter((candidate) =>
                          candidate === level
                            ? event.target.checked
                            : thinkingLevelSelection.includes(candidate)
                        ),
                      }))
                    }
                  />
                  <span>{t(`manualProviderModels.thinkingLevel.${level}`)}</span>
                </label>
              ))}
              <p className="field-help">{t('manualProviderModels.thinkingLevelsHelp')}</p>
              {thinkingLevelOptions.includes('off') &&
              !thinkingLevelSelection.includes('off') ? (
                <p className="warning">{t('manualProviderModels.thinkingLevelsOffHelp')}</p>
              ) : null}
            </div>
          ) : null}
          <label className="check-row" title={conflictingDefault?.displayName}>
            <input
              type="checkbox"
              checked={editor.defaultForProvider}
              disabled={Boolean(conflictingDefault) || selected?.status === 'disabled'}
              onChange={(event) =>
                setEditor((current) => ({
                  ...current,
                  defaultForProvider: event.target.checked,
                }))
              }
            />
            <span>{t('manualProviderModels.defaultForProvider')}</span>
          </label>
          {conflictingDefault ? (
            <p className="field-help">
              {t('manualProviderModels.otherDefault', {
                model: conflictingDefault.displayName,
              })}
            </p>
          ) : null}
          <details className="manual-model-routing-safety">
            <summary>{t('manualProviderModels.routingSafety')}</summary>
            <label className="check-row">
              <input
                type="checkbox"
                checked={editor.routingSafetyEnabled}
                disabled={selected?.status === 'disabled'}
                onChange={(event) =>
                  setEditor((current) => ({
                    ...current,
                    routingSafetyEnabled: event.target.checked,
                  }))
                }
              />
              <span>{t('manualProviderModels.routingSafetyExplicit')}</span>
            </label>
            {editor.routingSafetyEnabled ? (
              <div className="manual-model-capabilities">
                {(['low', 'medium', 'high'] as const).map((risk) => (
                  <label className="check-row" key={risk}>
                    <input
                      type="checkbox"
                      checked={editor.routingSafety.allowAdaptiveForRisk.includes(risk)}
                      disabled={selected?.status === 'disabled'}
                      onChange={(event) =>
                        setEditor((current) => ({
                          ...current,
                          routingSafety: {
                            ...current.routingSafety,
                            allowAdaptiveForRisk: event.target.checked
                              ? [...current.routingSafety.allowAdaptiveForRisk, risk]
                              : current.routingSafety.allowAdaptiveForRisk.filter(
                                  (item) => item !== risk
                                ),
                          },
                        }))
                      }
                    />
                    <span>{t('manualProviderModels.allowAdaptiveForRisk', { risk })}</span>
                  </label>
                ))}
                {(['confidential', 'secret'] as const).map((classification) => (
                  <label className="check-row" key={classification}>
                    <input
                      type="checkbox"
                      checked={editor.routingSafety.disallowForDataClassifications.includes(
                        classification
                      )}
                      disabled={selected?.status === 'disabled'}
                      onChange={(event) =>
                        setEditor((current) => ({
                          ...current,
                          routingSafety: {
                            ...current.routingSafety,
                            disallowForDataClassifications: event.target.checked
                              ? [
                                  ...current.routingSafety.disallowForDataClassifications,
                                  classification,
                                ]
                              : current.routingSafety.disallowForDataClassifications.filter(
                                  (item) => item !== classification
                                ),
                          },
                        }))
                      }
                    />
                    <span>
                      {t('manualProviderModels.disallowClassification', {
                        classification,
                      })}
                    </span>
                  </label>
                ))}
                {(['requireStrongForFinalAnswer', 'requireStrongForCompliance'] as const).map(
                  (field) => (
                    <label className="check-row" key={field}>
                      <input
                        type="checkbox"
                        checked={editor.routingSafety[field]}
                        disabled={selected?.status === 'disabled'}
                        onChange={(event) =>
                          setEditor((current) => ({
                            ...current,
                            routingSafety: {
                              ...current.routingSafety,
                              [field]: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>{t(`manualProviderModels.${field}`)}</span>
                    </label>
                  )
                )}
              </div>
            ) : (
              <p className="field-help">{t('manualProviderModels.routingSafetyInherited')}</p>
            )}
          </details>
          <FormField
            label={t('manualProviderModels.contextWindow')}
            type="number"
            min={1}
            disabled={immutable}
            value={editor.contextWindow}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, contextWindow: event.target.value }))
            }
          />
          <FormField
            label={t('manualProviderModels.maxOutputTokens')}
            type="number"
            min={1}
            disabled={immutable}
            value={editor.maxOutputTokens}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, maxOutputTokens: event.target.value }))
            }
          />
          <FormField
            label={t('manualProviderModels.sourceUrl')}
            type="url"
            value={editor.sourceUrl}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, sourceUrl: event.target.value }))
            }
          />
          <p className="field-help">{t('manualProviderModels.pricingEvidenceHelp')}</p>
          <FormField
            label={t('manualProviderModels.verifiedAt')}
            type="date"
            value={editor.verifiedAt}
            required
            onChange={(event) =>
              setEditor((current) => ({ ...current, verifiedAt: event.target.value }))
            }
          />
          <div className="manual-model-editor-actions">
            {selected ? (
              <button
                type="button"
                className="secondary danger"
                disabled={busy || selected.status === 'disabled'}
                onClick={() => void disable()}
              >
                {t('manualProviderModels.disable')}
              </button>
            ) : null}
            {selected ? (
              <button
                type="button"
                className="secondary"
                onClick={() => onOpenDeployment(selected)}
              >
                {t('manualProviderModels.openDeployment')}
              </button>
            ) : null}
            <button type="submit" disabled={busy || selected?.status === 'disabled'}>
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
